import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import Well from 'components/Well';
import { CALL_CENTER_PHONE_NUMBERS, PROPERTIES } from 'utils/constants';
import { HOST_BOOKING } from 'utils/routeUtils';
import { FormGroup, PopoverBody, Row } from 'reactstrap';
import LargestCol from 'components/LargestCol';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ifProp, prop } from 'styled-tools';
import { isMobileUserAgent } from 'utils/mobile';
import Select from 'components/Select';
import Button from 'components/Button';
import StyledPopover from 'components/StyledPopover';
import ErrorList from 'routes/DateSelection/components/styled/ErrorList';
import ContactUs from 'routes/DateSelection/components/styled/ContactUs';
import PhoneNumber from 'components/PhoneNumber';
import BookingDatesForm from './BookingDatesForm';
import FormLabel from './styled/FormLabel';

const H5 = styled.h5`
  font-size: 1em;
`;

const StyledButton = styled(Button)`
  font-size: 0.75rem;
  min-height: 1.875rem;
  margin-top: 1.5em;

  @media screen and (min-width: ${prop('theme.breakpoints.large')}) {
    margin-top: 0;
  }

  @media screen and (max-width: ${prop('theme.breakpoints.extraExtraLarge')}) {
    font-size: 0.625rem;
    padding: 0;
  }

  ${ifProp(
    'isHostBooking',
    css`
      @media screen and (max-width: ${prop('theme.breakpoints.medium')}) {
        margin-top: 1rem;
      }
    `
  )}
`;

const propTypes = {
  property: PropTypes.string,
  errors: PropTypes.array,
  startDate: PropTypes.any,
  onStartDateChange: PropTypes.func,
  endDate: PropTypes.any,
  onEndDateChange: PropTypes.func,
  roomType: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onRoomTypeChange: PropTypes.func,
  numberOfGuests: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  onNumGuestsChange: PropTypes.func,
  onNextClick: PropTypes.func,
  errorPopoverOpen: PropTypes.bool,
  closeErrorPopover: PropTypes.func,
  queryInput: PropTypes.shape({
    accountNumber: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    propertyCode: PropTypes.string
  }),
  isAdmin: PropTypes.bool,
  isWaitListBooking: PropTypes.bool,
  bookingSource: PropTypes.string,
  inMobileApp: PropTypes.bool
};

const defaultProps = {
  errors: [],
  isAdmin: false,
  inMobileApp: false
};

function BookYourStayForm({
  closeErrorPopover,
  endDate,
  errors,
  onRoomTypeChange,
  onNumGuestsChange,
  onNextClick,
  onStartDateChange,
  onEndDateChange,
  errorPopoverOpen,
  property,
  startDate,
  roomType,
  numberOfGuests,
  queryInput,
  isAdmin,
  bookingSource,
  isWaitListBooking,
  inMobileApp
}) {
  const isHostBooking = bookingSource === HOST_BOOKING;
  const isMobile = isMobileUserAgent();
  const buttonText = inMobileApp ? 'Book Room' : 'Next';

  return (
    <Well className="book-your-stay">
      {!(isAdmin && isHostBooking) && (
        <H5>
          BOOK YOUR STAY AT WIND CREEK {PROPERTIES[property].toUpperCase()}
        </H5>
      )}
      {(!isHostBooking || !isMobile) && (
        <BookingDatesForm
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={onStartDateChange}
          onEndDateChange={onEndDateChange}
          queryInput={queryInput}
          isHostBooking={isHostBooking}
          isAdmin={isAdmin}
        />
      )}
      <FormGroup row className="align-items-end">
        <LargestCol md="12" lg="6">
          <FormLabel htmlFor="numberOfGuests" isHostBooking={isHostBooking}>
            # of Guests
          </FormLabel>
          <Select
            value={numberOfGuests}
            onChange={onNumGuestsChange}
            clearable={false}
            backspaceRemoves={false}
            deleteRemoves={false}
            className="openUp"
            options={[
              { value: 1, label: 1 },
              { value: 2, label: 2 },
              { value: 3, label: 3 },
              { value: 4, label: 4 }
            ]}
            id="numberOfGuests"
          />
        </LargestCol>
        {onNextClick && (
          <LargestCol md="12" lg="6">
            <StyledButton
              id="book-your-stay-submit"
              onClick={onNextClick}
              block
              secondary
              isHostBooking={isHostBooking}
            >
              {isWaitListBooking ? 'WAITLIST' : buttonText}
            </StyledButton>
            {errors.length > 0 && (
              <StyledPopover
                placement="top-end"
                isOpen={errorPopoverOpen}
                target="book-your-stay-submit"
                className="errors"
                close={closeErrorPopover}
                trigger="focus"
              >
                <PopoverBody>
                  <Button className="close" onClick={closeErrorPopover}>
                    <FontAwesomeIcon icon="times" />
                  </Button>
                  <ErrorList>
                    {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ErrorList>
                </PopoverBody>
              </StyledPopover>
            )}
          </LargestCol>
        )}
      </FormGroup>
      {!isHostBooking && (
        <Row>
          <LargestCol md="12">
            <ContactUs>
              <small>
                Contact Us:{' '}
                <PhoneNumber value={CALL_CENTER_PHONE_NUMBERS[property]} />
              </small>
            </ContactUs>
          </LargestCol>
        </Row>
      )}
    </Well>
  );
}

BookYourStayForm.propTypes = propTypes;
BookYourStayForm.defaultProps = defaultProps;

export default BookYourStayForm;
